import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ButtonOutlined, Flex, Image, Text, SvgIcon } from 'Components/Base';
import ProductProgressCard from 'Components/ProductProgressCard';
import { useConfig } from 'Context/ConfigContext';
import { formatSemesterDateRanges } from 'Util/myPath';

import { mapProductProgressesToProductProgressCards } from './utils';

const EmptyStateWrapper = styled(Flex)(({ theme }) => ({
  height: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.myPath.gray[50],
  borderRadius: `${theme.radii[2]}px`,
}));

const ProductProgressCardWrapper = styled(Flex)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '320px',
  height: 'fit-content',
  flexDirection: 'column',
  gridRowGap: theme.space[2],
  overflowY: 'auto',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const StyledButtonOutlined = styled(ButtonOutlined)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  boxShadow: '0px 12px 24px 0px hsla(0, 0%, 0%, 0.1)',
  padding: `6px ${theme.space[3]}px`,
  alignItems: 'center',
  justifyContent: 'space-between',
  display: 'flex',
  width: 'fit-content',
  borderRadius: '8px',
  gap: `${theme.space[2]}px`,
  height: '32px',
  marginLeft: 'auto',
}));

const ResponsiveDisplayFlex = styled(Flex)(({ theme }) => ({
  display: 'none',
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    display: 'flex',
  },
}));

const MyPathProgressTracker = ({
  mpMyProgressTracker,
  goToMyPath,
  goToMyPathReport,
  onOpenSeeLastSemesterBlockProductsModal,
  hasPreviousSemester,
}) => {
  const { t } = useTranslation('myPath');

  const { myPath } = useConfig();

  const { products, semesterStartDate, semesterEndDate } = useMemo(() => {
    if (!mpMyProgressTracker) {
      return { products: [], semesterStartDate: null, semesterEndDate: null };
    }
    return {
      products: mapProductProgressesToProductProgressCards(
        mpMyProgressTracker.productProgresses,
        mpMyProgressTracker.studentSemester,
        myPath.imageUrl,
        myPath.prefixImageUrl
      ).sort((a, b) => b.isMigrated - a.isMigrated),
      semesterStartDate: mpMyProgressTracker.studentSemester.startDate,
      semesterEndDate: mpMyProgressTracker.studentSemester.endDate,
    };
  }, [mpMyProgressTracker, myPath.imageUrl, myPath.prefixImageUrl]);

  return (
    <Flex gridRowGap={2} flexDirection={'column'} width="100%" height="auto">
      <ResponsiveDisplayFlex
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        width="100%"
      >
        <Flex alignItems={'center'}>
          <Image src="/assets/images/myPath/myPathAnywhereLogo.svg" alt="MyPath Logo" />
          <Text textStyle={'subtitle1'} color={'myPath.primary.500'}>
            MyPath
          </Text>
        </Flex>
        {goToMyPath && (
          <Flex alignItems={'center'} gridColumnGap={2}>
            {myPath.reportUrl && (
              <StyledButtonOutlined onClick={goToMyPathReport} color="secondary">
                <SvgIcon icon={SvgIcon.ICON_LIST.EXCLAMATION_CIRCLE} width={20} height={20} />
                <Text textStyle={'body2'} fontWeight={'600'}>
                  {t('MILESTONE_TRACKER.REPORT_BUTTON')}
                </Text>
              </StyledButtonOutlined>
            )}
            <StyledButtonOutlined onClick={goToMyPath}>
              <Text textStyle={'body2'} fontWeight={'600'}>
                {t('MILESTONE_TRACKER.MYPATH_BUTTON')}
              </Text>
            </StyledButtonOutlined>
          </Flex>
        )}
      </ResponsiveDisplayFlex>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gridColumnGap={2}>
          <Text fontWeight={'600'} color={'myPath.textColor.primary'}>
            {t('MILESTONE_TRACKER.CURRENT_PLAN')}
          </Text>
          {onOpenSeeLastSemesterBlockProductsModal && hasPreviousSemester && (
            <StyledButtonOutlined onClick={onOpenSeeLastSemesterBlockProductsModal}>
              <Text textStyle={'body2'} fontWeight={'600'}>
                {t('MILESTONE_TRACKER.PREVIOUS_PLAN_BUTTON')}
              </Text>
            </StyledButtonOutlined>
          )}
        </Flex>
        {semesterStartDate && semesterEndDate && (
          <Text textStyle={'caption'} color={'myPath.textColor.secondary'}>
            {formatSemesterDateRanges(semesterStartDate, semesterEndDate)}
          </Text>
        )}
      </Flex>
      {products.length > 0 ? (
        <ProductProgressCardWrapper>
          {products.map((productProgress) => {
            const description = productProgress.isMigrated
              ? { text: '(ต่อจาก เทอมที่แล้ว)', color: 'myPath.primary.500' }
              : '';
            return (
              <ProductProgressCard
                key={productProgress.id}
                name={productProgress.name}
                subjectName={productProgress.subjectName}
                totalDuration={productProgress.totalDuration}
                percentComplete={productProgress.percentComplete}
                productType={productProgress.productType}
                thumbnailUrl={productProgress.thumbnailUrl}
                hasProduct={productProgress.hasProduct}
                milestones={productProgress.milestones}
                description={description}
                encouragementType={productProgress.encouragementType}
              />
            );
          })}
        </ProductProgressCardWrapper>
      ) : (
        <EmptyStateWrapper>
          <Text textStyle={'subtitle1'} fontWeight={'500'} color={'myPath.textColor.disabled'}>
            ไม่มีคอร์สเรียนที่ตรงกับแผนที่วางไว้
          </Text>
        </EmptyStateWrapper>
      )}
    </Flex>
  );
};

MyPathProgressTracker.propTypes = {
  goToMyPath: PropTypes.func,
  goToMyPathReport: PropTypes.func,
  mpMyProgressTracker: PropTypes.object,
  onOpenSeeLastSemesterBlockProductsModal: PropTypes.func,
  hasPreviousSemester: PropTypes.bool,
};

export default MyPathProgressTracker;
